import React, { createContext, useContext, useMemo, ReactNode } from 'react';

import { ListPriceConfig, FullCardOptionViewData } from '../types';

export type OptionContextValue = FullCardOptionViewData &
  ListPriceConfig & {
    hasMultipleOptions: boolean;
    shouldDisplayLabel: boolean;
  };

const OptionContext = createContext<OptionContextValue | null>(null);

export function useOptionContext(): OptionContextValue {
  const ctx = useContext(OptionContext);
  if (!ctx) {
    throw new Error('useOptionContext must be used within an OptionProvider');
  }
  return ctx;
}

type OptionProviderProps = {
  children: ReactNode;
  option: FullCardOptionViewData;
  listPrice: ListPriceConfig;
  hasMultipleOptions: boolean;
  shouldDisplayLabel: boolean;
};

/** Provides per-option data to tile primitives during iteration. */
export function OptionProvider({
  children,
  option,
  listPrice,
  hasMultipleOptions,
  shouldDisplayLabel,
}: OptionProviderProps): JSX.Element {
  const value = useMemo<OptionContextValue>(
    () => ({
      ...option,
      ...listPrice,
      hasMultipleOptions,
      shouldDisplayLabel,
    }),
    [option, listPrice, hasMultipleOptions, shouldDisplayLabel]
  );

  return <OptionContext.Provider value={value}>{children}</OptionContext.Provider>;
}
